import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, CalendarDays, ChevronDown, ChevronUp, Download, FileSignature, Hash, MapPinned, Pencil, Search, Undo2, UserRound, XCircle } from "lucide-react";
import { EmptyState } from "../common";
import { PaginationBar, TableRowsSkeleton } from "../PaginationBar";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "../FetchBar";
import { EMPTY_TERRITORY, TerritoryFilterSelects, territoryParams, useTerritoryMasters } from "../TerritoryFilter";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { api, downloadBlob, errMsg, fmtDateTime, istDateISO } from "../../lib/api";
import { cn } from "../../lib/utils";
import { SafActionBadge, ValuesPreview } from "./SafStatus";

/**
 * Phase 207 — SAF Approval and Modification Log.
 * One row per action on a citizen self-assessment: Submitted · Assigned · Verified by Collector · Demand Modified ·
 * Approved by Supervisor · Sent Back · Final Approved · Rejected — with the actor (name + ID), role, remarks and the
 * old / new demand values when something was modified. Municipal Admin: whole municipality; Supervisor: own wards only.
 * Endpoint: GET /t/{code}/reports/saf-log (+ /export for Excel).
 */
const ACTION_OPTIONS = [
  ["all", "All actions"], ["submitted", "Submitted"], ["assigned", "Assigned"], ["collector_verified", "Verified by Collector"],
  ["demand_modified", "Demand Modified"], ["approved", "Approved (any stage)"], ["supervisor_approved", "Approved by Supervisor"],
  ["final_approved", "Final Approved"], ["sent_back", "Sent Back for Correction"], ["rejected", "Rejected"],
];
const ROLE_OPTIONS = [["all", "All roles"], ["citizen", "Citizen"], ["collector", "Field Collector"], ["supervisor", "Supervisor"], ["municipal_admin", "Municipal Admin"]];
const PRESETS = [["Today", 0], ["7 days", 6], ["30 days", 29], ["90 days", 89]];
const daysAgoIST = (n) => istDateISO(new Date(Date.now() - n * 86400000));

function LogRow({ r, testId, propertyPath }) {
  const [open, setOpen] = useState(false);
  const hasValues = !!(r.old_values || r.new_values);
  return (
    <>
      <TableRow data-testid={`${testId}-row-${r.id}`} data-action={r.action}>
        <TableCell className="text-xs whitespace-nowrap tabular-nums align-top" data-testid={`${testId}-cell-time-${r.id}`}>{fmtDateTime(r.at)}</TableCell>
        <TableCell className="font-mono text-xs whitespace-nowrap align-top" data-testid={`${testId}-cell-saf-${r.id}`}>{r.saf_id || "—"}</TableCell>
        <TableCell className="font-mono text-xs font-medium whitespace-nowrap align-top" data-testid={`${testId}-cell-pin-${r.id}`}>
          {r.pin ? (propertyPath && r.property_id ? <a href={propertyPath(r)} className="text-primary hover:underline">{r.pin}</a> : r.pin) : <span className="text-muted-foreground font-sans">—</span>}
        </TableCell>
        <TableCell className="text-sm align-top max-w-[220px]" data-testid={`${testId}-cell-owner-${r.id}`}>
          <p className="font-medium truncate" title={r.owner_name || ""}>{r.owner_name || "—"}</p>
          {r.address && <p className="text-[11px] text-muted-foreground line-clamp-2 break-words" title={r.address}>{r.address}</p>}
        </TableCell>
        <TableCell className="text-xs whitespace-nowrap align-top" data-testid={`${testId}-cell-area-${r.id}`}>
          {r.zone_name || "—"}{r.ward_name ? <p className="text-muted-foreground">{r.ward_name}</p> : null}
        </TableCell>
        <TableCell className="align-top" data-testid={`${testId}-cell-action-${r.id}`}><SafActionBadge action={r.action} label={r.action_label} /></TableCell>
        <TableCell className="text-sm whitespace-nowrap align-top" data-testid={`${testId}-cell-by-${r.id}`}>{r.by_label || r.action_by_name || "—"}</TableCell>
        <TableCell className="text-xs whitespace-nowrap align-top" data-testid={`${testId}-cell-role-${r.id}`}>{r.action_by_role_label || "—"}</TableCell>
        <TableCell className="text-xs max-w-[280px] align-top" data-testid={`${testId}-cell-remarks-${r.id}`}>
          {r.remarks ? <p className="line-clamp-3 whitespace-normal break-words" title={r.remarks}>{r.remarks}</p> : <span className="text-muted-foreground">—</span>}
        </TableCell>
        <TableCell className="align-top text-right">
          {hasValues ? (
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOpen((v) => !v)} data-testid={`${testId}-values-toggle-${r.id}`} aria-expanded={open}>
              {open ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />} {r.old_values && r.new_values ? "Old / New" : "Values"}
            </Button>
          ) : <span className="text-xs text-muted-foreground">—</span>}
        </TableCell>
      </TableRow>
      {open && hasValues && (
        <TableRow className="bg-muted/30 hover:bg-muted/30" data-testid={`${testId}-values-${r.id}`}>
          <TableCell colSpan={10} className="py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl">
              <ValuesPreview values={r.old_values} title="Old values" testId={`${testId}-old-${r.id}`} />
              <ValuesPreview values={r.new_values} title={r.action === "final_approved" ? "Approved values" : "New values"} testId={`${testId}-new-${r.id}`} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function SafLogReport({ code, testId = "saf-log", propertyPath = null, className = "", scopedNote = null }) {
  const [dateFrom, setDateFrom] = useState(daysAgoIST(29));
  const [dateTo, setDateTo] = useState(istDateISO());
  const [area, setArea] = useState(EMPTY_TERRITORY);
  const [pin, setPin] = useState("");
  const [owner, setOwner] = useState("");
  const [safNo, setSafNo] = useState("");
  const [actionBy, setActionBy] = useState("");
  const [action, setAction] = useState("all");
  const [role, setRole] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const masters = useTerritoryMasters(code);

  useEffect(() => { setData(null); setError(null); setPage(1); setArea(EMPTY_TERRITORY); }, [code]);

  const filters = useMemo(() => ({ dateFrom, dateTo, zoneId: area.zoneId, wardId: area.wardId, pin: pin.trim(), owner: owner.trim(), safNo: safNo.trim(), actionBy: actionBy.trim(), action, role }),
    [dateFrom, dateTo, area.zoneId, area.wardId, pin, owner, safNo, actionBy, action, role]);
  const gate = useFetchGate(filters);
  const a = gate.applied;
  useEffect(() => { setPage(1); }, [gate.appliedKey]);

  const params = useCallback((f) => ({
    date_from: f.dateFrom || undefined, date_to: f.dateTo || undefined,
    ...territoryParams({ zoneId: f.zoneId, wardId: f.wardId }),
    pin: f.pin || undefined, owner_name: f.owner || undefined, saf_id: f.safNo || undefined, action_by: f.actionBy || undefined,
    action: f.action, role: f.role,
  }), []);

  const load = useCallback(() => {
    if (!a || !code) return;
    setLoading(true); setError(null);
    api.get(`/t/${code}/reports/saf-log`, { params: { ...params(a), page, limit } })
      .then((r) => setData(r.data))
      .catch((e) => { setError(errMsg(e)); setData(null); })
      .finally(() => setLoading(false));
  }, [code, a, params, page, limit]);
  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version]);

  const doExport = async () => {
    if (!a) return;
    setExporting(true);
    try {
      const q = Object.fromEntries(Object.entries(params(a)).filter(([, v]) => v !== undefined && v !== ""));
      await downloadBlob(`/t/${code}/reports/saf-log/export?${new URLSearchParams(q).toString()}`,
        `saf-approval-log_${a.dateFrom || "all"}_to_${a.dateTo || "all"}.xlsx`);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const setRange = (from, to) => { setDateFrom(from); setDateTo(to); };
  const activePreset = PRESETS.find(([, n]) => dateFrom === daysAgoIST(n) && dateTo === istDateISO())?.[0];
  const items = data?.items || [];
  const summary = data?.summary || {};
  const hasExtra = !!(area.zoneId || area.wardId || pin || owner || safNo || actionBy || action !== "all" || role !== "all");

  return (
    <div className={cn("space-y-4", className)} data-testid={`${testId}-report`}>
      <FetchForm onFetch={gate.fetch} className="flex flex-wrap items-center gap-3" testId={`${testId}-filter-form`}>
        <div className="flex items-center gap-1.5" data-testid={`${testId}-date-range`}>
          <CalendarDays className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input type="date" value={dateFrom} className="w-40" aria-label="From date" max={dateTo || undefined}
            onChange={(e) => setRange(e.target.value, dateTo)} data-testid={`${testId}-date-from`} />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={dateTo} className="w-40" aria-label="To date" min={dateFrom || undefined}
            onChange={(e) => setRange(dateFrom, e.target.value)} data-testid={`${testId}-date-to`} />
        </div>
        <div className="inline-flex rounded-md border bg-muted/40 p-0.5" role="group" aria-label="Quick ranges" data-testid={`${testId}-date-presets`}>
          {PRESETS.map(([label, n]) => (
            <Button key={label} type="button" size="sm" variant={activePreset === label ? "default" : "ghost"} className="h-7 px-2.5 text-xs"
              onClick={() => setRange(daysAgoIST(n), istDateISO())} data-testid={`${testId}-preset-${label.replace(/\s+/g, "-").toLowerCase()}`}>
              {label}
            </Button>
          ))}
        </div>
        <div className="w-full lg:w-[420px]">
          <TerritoryFilterSelects masters={masters} value={area} onChange={setArea} levels={["zone", "ward"]} testIdPrefix={testId} />
        </div>
        <div className="relative">
          <Hash className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 w-40" placeholder="PIN" value={pin} aria-label="PIN" onChange={(e) => setPin(e.target.value)} data-testid={`${testId}-pin`} />
        </div>
        <div className="relative">
          <FileSignature className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 w-44" placeholder="SAF No" value={safNo} aria-label="SAF number" onChange={(e) => setSafNo(e.target.value)} data-testid={`${testId}-saf-no`} />
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 w-48" placeholder="Owner name" value={owner} aria-label="Owner name" onChange={(e) => setOwner(e.target.value)} data-testid={`${testId}-owner`} />
        </div>
        <div className="relative">
          <UserRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 w-52" placeholder="Action by — name or ID" value={actionBy} aria-label="Action by name or employee ID"
            onChange={(e) => setActionBy(e.target.value)} data-testid={`${testId}-action-by`} />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-52" aria-label="Action type" data-testid={`${testId}-action-select`}><SelectValue /></SelectTrigger>
          <SelectContent>{ACTION_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-action-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-44" aria-label="Role" data-testid={`${testId}-role-select`}><SelectValue /></SelectTrigger>
          <SelectContent>{ROLE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-role-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {hasExtra && (
          <Button type="button" variant="ghost" size="sm" className="text-muted-foreground"
            onClick={() => { setArea(EMPTY_TERRITORY); setPin(""); setOwner(""); setSafNo(""); setActionBy(""); setAction("all"); setRole("all"); }} data-testid={`${testId}-clear-filters`}>
            Clear filters
          </Button>
        )}
        <FetchButton type="submit" size="sm" loading={loading} dirty={gate.dirty} fetched={gate.fetched} disabled={!code} testId={`${testId}-fetch-button`} />
        <FetchDirtyHint dirty={gate.dirty} testId={`${testId}-fetch-dirty`} />
        <Button type="button" variant="outline" size="sm" className="h-10 ml-auto" onClick={doExport}
          disabled={!gate.fetched || exporting || loading || !data || data.total === 0} data-testid={`${testId}-export-button`}>
          <Download className="h-4 w-4 mr-1.5" aria-hidden="true" /> {exporting ? "Preparing…" : "Export to Excel"}
        </Button>
      </FetchForm>

      {scopedNote && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`${testId}-scope-note`}><MapPinned className="h-3.5 w-3.5" aria-hidden="true" /> {scopedNote}</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid={`${testId}-summary`}>
        {[["Total actions", summary.total, `${testId}-stat-total`, null],
          ["Submitted", summary.submitted, `${testId}-stat-submitted`, FileSignature],
          ["Demand modified", summary.modified, `${testId}-stat-modified`, Pencil],
          ["Approved", summary.approved, `${testId}-stat-approved`, BadgeCheck],
          ["Sent back", summary.sent_back, `${testId}-stat-sent-back`, Undo2],
          ["Rejected", summary.rejected, `${testId}-stat-rejected`, XCircle]].map(([l, v, tid, Icon]) => (
          <Card key={l} className="shadow-sm">
            <CardContent className="p-3.5 flex items-start justify-between gap-2">
              <div>
                <p className="text-[11px] text-muted-foreground">{l}</p>
                <p className="text-xl font-semibold tabular-nums" data-testid={tid}>{gate.fetched && data ? (v ?? 0).toLocaleString("en-IN") : "—"}</p>
              </div>
              {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-1" aria-hidden="true" />}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {!gate.fetched ? (
            <FetchPrompt onFetch={gate.fetch} testId={`${testId}-fetch-prompt`}
              subtitle="Pick a date range, Zone / Ward, PIN, owner, SAF number, who acted, the action type and role above, then click Fetch to load the log." />
          ) : error ? (
            <EmptyState title="Could not load the SAF log" subtitle={error}
              action={<Button variant="outline" onClick={load} data-testid={`${testId}-retry`}>Try again</Button>} />
          ) : loading && !data ? (
            <div className="p-4"><TableRowsSkeleton rows={8} cols={10} testId={`${testId}-skeleton`} /></div>
          ) : items.length === 0 ? (
            <EmptyState title="No SAF actions in this range" subtitle="Nothing matches these filters. Widen the date range or clear a filter." />
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid={`${testId}-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Date and Time</TableHead>
                    <TableHead className="whitespace-nowrap">SAF No</TableHead>
                    <TableHead>PIN</TableHead>
                    <TableHead className="whitespace-nowrap">Owner / Address</TableHead>
                    <TableHead className="whitespace-nowrap">Zone / Ward</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="whitespace-nowrap">Action By (Name and ID)</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="min-w-[200px]">Remarks / Reason</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Old / New values</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => <LogRow key={r.id} r={r} testId={testId} propertyPath={propertyPath} />)}
                </TableBody>
              </Table>
            </div>
          )}
          {gate.fetched && data && data.total > 0 && (
            <div className="px-4 pb-4">
              <PaginationBar page={page} limit={limit} total={data.total} loading={loading}
                onPageChange={setPage} onLimitChange={(n) => { setLimit(n); setPage(1); }} testId={`${testId}-pagination`} />
            </div>
          )}
        </CardContent>
      </Card>
      {gate.fetched && data && (
        <p className="text-[11px] text-muted-foreground" data-testid={`${testId}-footnote`}>
          Every action on a citizen self-assessment is recorded here and cannot be edited. The PIN appears on all rows of a self-assessment once it is finally approved. Dates and times in IST.
        </p>
      )}
    </div>
  );
}
